import { z } from "zod";
import { ServiceTemplateRecurrencePeriod } from "./serviceTemplateRecurrencePeriod";
import {
    MonthRecurrenceType,
    QuarterMonth,
    RecurrenceLeadUnit,
    RelativeCalendarPeriod,
    RelativeCalendarPosition,
    RelativeDayType,
    ServiceTemplateRecurrenceStatus,
    WorkEndDateMode,
    WorkStartDateMode,
} from "./serviceTemplateRecurrence";

/**
 * API response shape for a service-template schedule (a.k.a.
 * ServiceTemplateRecurrenceEntity). The audit timestamps `createdAt`/`updatedAt`
 * use `z.coerce.date()`, so their JSON-serialised strings become Date objects on
 * the client. The date columns (`startDate`, `endDate`, `generatedUntil`) use
 * `z.string().date()` and remain ISO date strings, matching the entity's
 * `type: "date"` string columns.
 */
export const ServiceTemplateScheduleSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    workspaceId: z.string().uuid().nullable(),
    templateId: z.string().uuid(),
    entityUuid: z.string().uuid(),
    groupId: z.string().uuid().nullable(),
    period: z.nativeEnum(ServiceTemplateRecurrencePeriod),
    interval: z.number().int().positive(),
    startDate: z.string().date(),
    endDate: z.string().date().nullable(),
    generatedUntil: z.string().date().nullable(),
    maxOccurrenceCount: z.number().int().positive().nullable(),
    status: z.nativeEnum(ServiceTemplateRecurrenceStatus),
    applicationLeadValue: z.number().int().nonnegative(),
    applicationLeadUnit: z.nativeEnum(RecurrenceLeadUnit),
    weekDay: z.number().int().min(0).max(6).nullable(),
    weekDays: z.array(z.number().int().min(0).max(6)).nullable(),
    month: z.number().int().min(1).max(12).nullable(),
    dateOfMonth: z.number().int().min(1).max(31).nullable(),
    datesOfMonth: z.array(z.number().int().min(1).max(31)).nullable(),
    monthRecurrenceType: z.nativeEnum(MonthRecurrenceType).nullable(),
    quarterMonth: z.nativeEnum(QuarterMonth).nullable(),
    workStartDateMode: z.nativeEnum(WorkStartDateMode),
    workStartOffsetDays: z.number().int().nonnegative().nullable(),
    workStartSkipWeekend: z.boolean(),
    workStartWeekday: z.number().int().min(1).max(5).nullable(),
    workStartRelativePeriod: z.nativeEnum(RelativeCalendarPeriod).nullable(),
    workStartRelativePosition: z
        .nativeEnum(RelativeCalendarPosition)
        .nullable(),
    workStartRelativeDayType: z.nativeEnum(RelativeDayType).nullable(),
    workStartRelativeDayOfPeriod: z.number().int().positive().nullable(),
    workEndDateMode: z.nativeEnum(WorkEndDateMode),
    workEndOffsetDays: z.number().int().nonnegative().nullable(),
    workEndRelativePeriod: z.nativeEnum(RelativeCalendarPeriod).nullable(),
    workEndRelativePosition: z.nativeEnum(RelativeCalendarPosition).nullable(),
    workEndRelativeDayType: z.nativeEnum(RelativeDayType).nullable(),
    workEndRelativeDayOfPeriod: z.number().int().positive().nullable(),
    preparerId: z.number().int().nullable(),
    reviewerId: z.number().int().nullable(),
    managerId: z.number().int().nullable(),
    clientId: z.number().int().nullable(),
    createdByUserId: z.number().int().nullable(),
    updatedByUserId: z.number().int().nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    // The following `next*` fields all describe the SAME occurrence: the
    // earliest SCHEDULED occurrence on or after today (past due SCHEDULED
    // shells — auto-apply lag — are excluded). Null on every field together
    // when no such occurrence exists (e.g. paused with nothing upcoming, or
    // every future shell already applied). List responses only.
    nextApplicationDate: z.string().date().nullable().optional(),
    nextServiceStartDate: z.string().date().nullable().optional(),
    nextServiceEndDate: z.string().date().nullable().optional(),
    nextWorkStartDate: z.string().date().nullable().optional(),
    nextWorkEndDate: z.string().date().nullable().optional(),
});

export type ServiceTemplateSchedule = z.infer<
    typeof ServiceTemplateScheduleSchema
>;
